import React, { useState } from 'react';
import DocumentPicker from './DocumentPicker';
import { useToast } from './ToastContext';
import { FaTimes } from 'react-icons/fa';
const TimesIcon = FaTimes as unknown as React.FC<{ className?: string }>;

const API_BASE = '/api';

interface DocOption {
  id: string;
  title: string;
  reference_code: string | null;
  category_name: string;
}

interface UpdateRow {
  tempId: string;
  file: File;
  currentTitle: string;
  isRenaming: boolean;
  newTitle: string;
  targetDocumentId: string | null;
  individualReason: string;
  versionNumber: string;
}

function cleanTitle(filename: string): string {
  const name = filename.replace(/\.[^/.]+$/, '');
  return name.replace(/_+/g, ' ').replace(/\s+/g, ' ').trim();
}

interface BulkUpdatePanelProps {
  allDocuments: DocOption[];
}

function BulkUpdatePanel({ allDocuments }: BulkUpdatePanelProps) {
  const { showToast } = useToast();
  const [rows, setRows] = useState<UpdateRow[]>([]);
  const [batchReason, setBatchReason] = useState('');
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commitResult, setCommitResult] = useState<{
    created_document_ids: string[];
    errors: { file: string; reason: string }[];
  } | null>(null);

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    const newRows: UpdateRow[] = newFiles.map((file) => ({
      tempId: `${file.name}-${Date.now()}-${Math.random()}`,
      file,
      currentTitle: '',
      isRenaming: false,
      newTitle: '',
      targetDocumentId: null,
      individualReason: '',
      versionNumber: '',
    }));
    setRows((prev) => [...prev, ...newRows]);
    e.target.value = '';
  };

  const removeRow = (tempId: string) => {
    setRows((prev) => prev.filter((r) => r.tempId !== tempId));
  };

  const updateRow = (tempId: string, changes: Partial<UpdateRow>) => {
    setRows((prev) => prev.map((r) => (r.tempId === tempId ? { ...r, ...changes } : r)));
  };

  const updateRowTarget = async (tempId: string, targetDocumentId: string) => {
    const selectedDoc = allDocuments.find((d) => d.id === targetDocumentId);

    updateRow(tempId, {
      targetDocumentId,
      currentTitle: selectedDoc?.title || '',
      isRenaming: false,
      newTitle: '',
      versionNumber: '',
    });

    const res = await fetch(`${API_BASE}/documents/${targetDocumentId}/versions`, { credentials: 'include' });
    const versions = await res.json();
    const currentMax = Math.max(...versions.map((v: any) => parseFloat(v.version_number)), 0);
    updateRow(tempId, { versionNumber: (currentMax + 1).toString() });
  };

  const effectiveReason = (row: UpdateRow) =>
    row.individualReason.trim() || batchReason.trim();

  const isValid =
    rows.length > 0 &&
    rows.every((r) => r.targetDocumentId && r.versionNumber.trim().length > 0);

  const handleCommit = async () => {
    if (!isValid) return;

    setCommitting(true);
    setError(null);

    try {
      const formData = new FormData();
      rows.forEach((r) => formData.append('files', r.file));

      const itemsMeta = rows.map((r) => ({
        title: r.isRenaming && r.newTitle.trim() && r.newTitle.trim() !== r.currentTitle
          ? r.newTitle.trim()
          : undefined,
        target_document_id: r.targetDocumentId,
        change_notes: effectiveReason(r),
        version_number: r.versionNumber,
      }));

      formData.append('items_meta', JSON.stringify(itemsMeta));

      const res = await fetch(`${API_BASE}/imports/bulk-update-commit`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      setCommitResult(data);

      if (data.errors && data.errors.length > 0) {
        showToast(`Completed with ${data.errors.length} error(s).`, 'error');
      } else {
        showToast(`${data.created_document_ids.length} document(s) updated successfully.`);
      }
    } catch (err: any) {
      setError('Commit failed');
      showToast('Bulk update failed.', 'error');
    } finally {
      setCommitting(false);
    }
  };

  if (commitResult) {
    return (
      <div>
        <h2>Bulk Update Complete</h2>
        <p>{commitResult.created_document_ids.length} document(s) updated.</p>
        {commitResult.errors.length > 0 && (
          <>
            <p className="error">{commitResult.errors.length} file(s) failed:</p>
            <ul>
              {commitResult.errors.map((e, idx) => (
                <li key={idx}>{e.file} — {e.reason}</li>
              ))}
            </ul>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="form-field">
        <label>Batch Change Reason</label>
        <input
            type="text"
            value={batchReason}
            onChange={(e) => {
            const newValue = e.target.value;
            setBatchReason(newValue);
            // auto-fill every row's individual reason to match, so it's visible and editable per-row
            setRows((prev) => prev.map((r) => ({ ...r, individualReason: newValue })));
            }}
            placeholder="e.g. Annual 2027 policy refresh — applies to all files unless overridden below"
        />
      </div>

      <div className="form-field">
        <label>Files</label>
        <label className="file-upload-button">
          Choose Files
          <input type="file" multiple className="file-upload-input" onChange={handleFilesSelected} />
        </label>
      </div>

      {rows.length > 0 && (
        <div className="doc-table-wrapper bulk-new-docs-table">
          <table className="doc-table">
            <thead>
              <tr>
                <th className="col-filename">File</th>
                <th className="col-name">Document Name</th>
                <th className="col-target">Update Which Document?</th>
                <th className="col-version">Version</th>
                <th className="col-reason">Reason (optional override)</th>
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.tempId}>
                  <td className="col-filename">{row.file.name}</td>
                  <td className="col-name">
                    {row.isRenaming ? (
                      <input
                        type="text"
                        value={row.newTitle}
                        onChange={(e) => updateRow(row.tempId, { newTitle: e.target.value })}
                        autoFocus
                      />
                    ) : (
                      <div className="rename-display">
                        <span>{row.currentTitle || '—'}</span>
                        {row.targetDocumentId && (
                          <button
                            type="button"
                            className="rename-toggle"
                            title="Rename"
                            onClick={() => updateRow(row.tempId, { isRenaming: true, newTitle: row.currentTitle })}
                          >
                            ✎
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="col-target">
                    <DocumentPicker
                      documents={allDocuments}
                      value={row.targetDocumentId}
                      onChange={(docId) => updateRowTarget(row.tempId, docId)}
                    />
                  </td>
                  <td className="col-version">
                    <input
                      type="text"
                      value={row.versionNumber}
                      onChange={(e) => updateRow(row.tempId, { versionNumber: e.target.value })}
                      placeholder="e.g. 2 or 1.1"
                    />
                  </td>
                  <td className="col-reason">
                    <input
                      type="text"
                      value={row.individualReason}
                      onChange={(e) => updateRow(row.tempId, { individualReason: e.target.value })}
                      placeholder="Uses batch reason if left blank"
                    />
                  </td>
                  <td className="col-actions">
                    <button className="icon-button icon-button-danger" title="Remove" onClick={() => removeRow(row.tempId)}>
                      <TimesIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && <p className="error">{error}</p>}

      <button
        className="btn-primary btn-commit"
        onClick={handleCommit}
        disabled={committing || !isValid}
      >
        {committing ? 'Committing...' : `Commit ${rows.length} document(s)`}
      </button>
    </div>
  );
}

export default BulkUpdatePanel;